// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * Presents one guidance rule for scanning and optional detailed review.
 *
 * The card derives title, preview, risk, and body from the canonical guidance
 * parser so list presentation never rewrites stored markdown. Edit/delete
 * authority remains explicit in props from the owning knowledge-base screen.
 */
import { useState } from 'react'
import { ChevronDown, Edit, Trash2 } from 'lucide-react'

import { Button, MarkdownViewer } from '@/components/ui'
import type { KnowledgeBaseItem } from '../types'
import { parseGuidanceContent, riskLevel } from './guidanceContent'

const RISK_STYLE = {
  high: 'bg-red-50 text-red-700',
  medium: 'bg-amber-50 text-amber-700',
  low: 'bg-green-50 text-green-700',
}

interface GuidanceItemCardProps {
  item: KnowledgeBaseItem
  readOnly: boolean
  disabled: boolean
  saving: boolean
  onEdit: () => void
  onDelete: () => void
}

export function GuidanceItemCard({
  item,
  readOnly,
  disabled,
  saving,
  onEdit,
  onDelete,
}: GuidanceItemCardProps) {
  const content = parseGuidanceContent(item.content)
  const hasBody = content.body.length > 0
  const [expanded, setExpanded] = useState(false)
  const risk = riskLevel(item.score)

  return (
    <li
      data-testid={`kb-row-${item.id}`}
      className="overflow-hidden rounded-xl border border-slate-200 bg-white transition-[box-shadow,border-color] duration-150 hover:border-[#d7dee8] hover:shadow-[0_2px_10px_rgba(15,23,42,.05)]"
    >
      <div
        className={`flex items-start gap-3.5 px-4 py-3.5 ${hasBody ? 'cursor-pointer' : ''}`}
        onClick={() => hasBody && setExpanded(value => !value)}
      >
        <span className="shrink-0 rounded-lg bg-slate-100 px-2.5 py-1 font-mono text-xs font-semibold text-slate-600">
          {item.id}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2.5">
            <h4 className="text-sm font-semibold text-slate-900">{content.title || `Guidance item ${item.id}`}</h4>
            <span className={`rounded-full px-2.5 py-[3px] text-[11px] font-semibold tracking-[.02em] ${RISK_STYLE[risk]}`}>
              {risk[0].toUpperCase() + risk.slice(1)} risk
            </span>
          </div>
          {!expanded && content.preview && (
            <p className="mt-1.5 line-clamp-1 text-[13px] leading-[1.55] text-slate-600">{content.preview}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-0.5" onClick={event => event.stopPropagation()}>
          {!readOnly && (
            <>
              <Button
                type="button"
                aria-label={`Edit item ${item.id}`}
                variant="ghost"
                size="icon"
                onClick={onEdit}
                disabled={disabled || saving}
                className="h-[30px] w-[30px] rounded-md text-slate-600 hover:bg-slate-100"
                data-testid={`kb-item-edit-${item.id}`}
              >
                <Edit className="h-3.5 w-3.5" />
              </Button>
              <Button
                type="button"
                aria-label={`Delete item ${item.id}`}
                variant="ghost"
                size="icon"
                onClick={onDelete}
                disabled={disabled || saving}
                className="h-[30px] w-[30px] rounded-md text-red-600 hover:bg-red-50 hover:text-red-600"
                data-testid={`kb-item-delete-${item.id}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
          {hasBody && (
            <button
              type="button"
              aria-label={expanded ? `Collapse item ${item.id}` : `Expand item ${item.id}`}
              aria-expanded={expanded}
              onClick={() => setExpanded(value => !value)}
              className="inline-flex h-[30px] w-[30px] items-center justify-center text-slate-400"
            >
              <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
            </button>
          )}
        </div>
      </div>
      {expanded && (
        <div className="docreview-kb-details border-t border-slate-100 bg-[#fbfcfd] pb-4 pl-[62px] pr-[18px] pt-3.5 text-[13px] leading-[1.55] text-slate-700">
          <MarkdownViewer content={content.body} />
        </div>
      )}
    </li>
  )
}
